import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  canAccessRoute,
  getMissingPermissions,
  Permission,
  hasWildcardPermission,
} from "../utils/permissions";
import LoadingComponent from "../components/LoadingComponent";

interface PermissionRouteProps {
  children: React.ReactNode;
  requiredPermissions?: Permission[];
  requireAll?: boolean;
  fallbackPath?: string;
}

/**
 * PermissionRoute Component
 *
 * Protects routes based on user permissions
 * - First checks if user is authenticated
 * - Then checks if user has required permissions (including wildcard support)
 * - Redirects to unauthorized page if permissions are missing
 *
 * Usage:
 * <PermissionRoute requiredPermissions={[PERMISSIONS.VIEW_USERS]}>
 *   <UsersPage />
 * </PermissionRoute>
 */
const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  requiredPermissions,
  requireAll = false,
  fallbackPath = "/unauthorized",
}) => {
  const { isAuthenticated, isLoading, permissions } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  // if (isLoading) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
  //       <div className="text-center">
  //         <div className="inline-block w-16 h-16 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mb-4"></div>
  //         <p className="text-gray-600 text-lg font-medium">
  //           Checking permissions...
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }

  /* Loading State */
  if (isLoading) {
    return <LoadingComponent title="" message="Checking permissions..." />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // Debug logging
  // // console.log("🔐 PermissionRoute Check:", {
  //   path: location.pathname,
  //   userPermissions: permissions,
  //   requiredPermissions,
  //   hasWildcard: hasWildcardPermission(permissions),
  // });

  // Check for wildcard permissions first - grants access to everything
  if (hasWildcardPermission(permissions)) {
    // // console.log("✅ Access granted via wildcard permission");
    return <>{children}</>;
  }

  // If specific permissions are provided, check them
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasRequiredPerms = requireAll
      ? requiredPermissions.every((permission) =>
          permissions?.includes(permission),
        )
      : requiredPermissions.some((permission) =>
          permissions?.includes(permission),
        );

    // console.log("🔍 Required permissions check:", {
    //   required: requiredPermissions,
    //   requireAll,
    //   hasRequiredPerms,
    // });

    if (!hasRequiredPerms) {
      // // console.log("❌ Access denied - missing required permissions");
      return (
        <Navigate
          to={fallbackPath}
          state={{ from: location, missingPermissions: requiredPermissions }}
          replace
        />
      );
    }
  }

  // Check if user can access the current route
  const hasRouteAccess = canAccessRoute(permissions, location.pathname);
  // console.log("🛣️ Route access check:", { hasRouteAccess });

  if (!hasRouteAccess) {
    const missing = getMissingPermissions(permissions, location.pathname);
    // console.log("❌ Access denied - missing route permissions:", missing);
    return (
      <Navigate
        to={fallbackPath}
        state={{ from: location, missingPermissions: missing }}
        replace
      />
    );
  }

  // console.log("✅ Access granted");
  return <>{children}</>;
};

export default PermissionRoute;
